import {
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { workspaceName } from '../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useCloudPlatformDiscovery } from '../utils/hooks';
import { type Job, jobName } from './gen/ai/h2o/mlops/batch/v1/job_pb';
import type {
  CancelJobRequest,
  DeleteJobRequest,
  ListJobsRequest,
  ListJobsResponse,
} from './gen/ai/h2o/mlops/batch/v1/job_service_pb';
import { cancelJob, deleteJob, listJobs } from './mlops.batch.functions';

const cacheKeys = {
  jobsAll: (workspaceId: string, params?: Pick<ListJobsRequest, `filter` | `orderBy`>) => {
    const baseKey: QueryKey = [`mlops`, `batch`, workspaceId, `jobs`, `all`];
    if (!params) return baseKey;
    const { filter, orderBy } = params;
    return [...baseKey, { filter: filter || ``, orderBy: orderBy || `` }];
  },
  jobOne: (workspaceId: string, jobId: string) => [`mlops`, `batch`, workspaceId, `jobs`, `one`, jobId] as const,
};

export const useMutationCancelJob = (
  options?: Omit<UseMutationOptions<Job, unknown, CancelJobRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      const response = await cancelJob(mlopsApiUrl, variables);
      const cancelledJob = response.job!;
      const { job: jobId, workspace: workspaceId } = jobName.parse(variables.name);
      queryClient.invalidateQueries(cacheKeys.jobOne(workspaceId, jobId));
      queryClient.invalidateQueries({
        queryKey: cacheKeys.jobsAll(workspaceId),
        exact: false,
      });
      return cancelledJob;
    },
  });
};

export const useMutationDeleteJob = (
  options?: Omit<UseMutationOptions<void, unknown, DeleteJobRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      await deleteJob(mlopsApiUrl, variables);
      const { workspace: workspaceId, job: jobId } = jobName.parse(variables.name);
      queryClient.removeQueries(cacheKeys.jobOne(workspaceId, jobId));
      queryClient.invalidateQueries({
        queryKey: cacheKeys.jobsAll(workspaceId),
        exact: false,
      });
    },
  });
};

export const useQueryJobs = <TData = ListJobsResponse>(
  workspaceId: string,
  params?: Pick<ListJobsRequest, `filter` | `orderBy` | `pageSize`>,
  options?: Omit<UseInfiniteQueryOptions<ListJobsResponse, unknown, TData>, `queryFn` | `queryKey` | `getNextPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const baseOptions: UseInfiniteQueryOptions<ListJobsResponse, unknown, TData> = {
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
    queryFn: ({ signal, pageParam }) => {
      const parent = workspaceName.compile({ workspace: workspaceId });
      return listJobs(mlopsApiUrl, { ...params, pageToken: pageParam, parent }, { signal });
    },
    queryKey: cacheKeys.jobsAll(workspaceId, params),
  };
  return useInfiniteQuery({
    ...options,
    ...baseOptions,
  });
};
